/**
 * @fileoverview Google login ("Log in using Google") — AR-933
 * @description Browser side of the Google login of the sign-in page. The
 * Firebase popup identifies the Google account; the Firebase user is then
 * posted to the backend, which opens the Devise session only when the account
 * has Google login enabled. The backend answers 401 with an `error_code` when
 * it refuses, and this module maps that answer to a reason the UI can explain:
 * a password account that never linked Google (or disconnected it in the
 * profile, AR-510) must sign in with username and password first.
 */

import axios from 'axios'

/** DeviseTokenAuth session endpoint; with a `firebaseUser` body it is the Google login */
export const GOOGLE_SIGNIN_ENDPOINT = '/api/auth/sign_in'

/** Mirrors the backend `error_code` contract (401 body) */
export type GoogleSigninFailure = 'google_login_not_enabled' | 'invalid_credentials' | 'unknown'

export type GoogleSigninResult = { success: true } | { success: false; reason: GoogleSigninFailure }

/** Body of a refused POST /api/auth/sign_in */
interface GoogleSigninFailureBody {
  errors?: string[]
  error_code?: string
  success?: boolean
}

const failureFor = (status?: number, errorCode?: string): GoogleSigninFailure => {
  if (errorCode === 'google_login_not_enabled') return 'google_login_not_enabled'
  if (status === 401) return 'invalid_credentials'
  return 'unknown'
}

/** i18n key of the message shown for a refused Google login */
export const googleSigninErrorKey = (reason: GoogleSigninFailure): string => `google_signin.errors.${reason}`

/**
 * Opens the backend session for the Firebase user. Plain axios (not the `api`
 * helper) on purpose: the helper swallows 401s, and this call needs the raw
 * status and body to tell the user what went wrong.
 */
export async function googleSignin(firebaseUser: unknown): Promise<GoogleSigninResult> {
  try {
    await axios.post(GOOGLE_SIGNIN_ENDPOINT, { firebaseUser }, { withCredentials: true })
    return { success: true }
  } catch (error) {
    const response = axios.isAxiosError(error) ? error.response : undefined
    const body = response?.data as GoogleSigninFailureBody | undefined
    return { success: false, reason: failureFor(response?.status, body?.error_code) }
  }
}
